import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/phare')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Phare Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Phare: both check every 30 seconds. Phare uses usage-based pricing with a free tier; Uptrack has 5 free monitors and flat plans from $3/mo." },
      { property: 'og:title', content: 'Phare Alternative — Uptrack' },
      { property: 'og:description', content: "Phare and Uptrack both offer 30s checks. Phare bills by usage; Uptrack uses flat plans. Compare features and pricing." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-phare.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/phare' },
    ],
    scripts: comparisonScripts('phare', "Phare"),
  }),
})

const data: ComparisonData = {
  competitor: "Phare",
  tagline: "Phare is a monitoring tool with 30-second checks, status pages, and AI incident summaries, billed by usage. Uptrack checks every 30 seconds with flat plans from $3/mo.",
  heroDescription: "These facts come from Phare's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "Hobby: 100,000 monitoring events/mo" },
    { feature: "Monthly bill", uptrack: "Fixed per plan", competitor: "Varies with usage (Scale: €5/mo plus events)", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "30 sec" },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "1-5 consecutive failures (default 2)" },
    { feature: "Check regions", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "12", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Unlimited, with custom domains, on all plans" },
    { feature: "Team members", uptrack: "3 on Pro, 5 on Team, 15 on Business", competitor: "Unlimited" },
    { feature: "AI incident summaries", uptrack: "Not available", competitor: "10/mo free, then billed per summary" },
    { feature: "Browser checks", uptrack: "Playwright (5 on Pro)", competitor: "Not offered (HTTP, TCP, ICMP checks)", uptrackWins: true },
  ],

  painPoints: [
    {
      title: "Usage-based billing",
      description: "Phare's Scale plan is €5/mo plus €0.00001 per monitoring event above 500,000. AI summaries and SMS or voice alerts are also billed per use. Your bill moves with check volume. Uptrack plans have a fixed monthly price.",
    },
    {
      title: "Browser checks",
      description: "Phare checks HTTP, TCP, and ICMP. Uptrack also runs Playwright browser monitors (5 on Pro) for flows like login or checkout.",
    },
  ],

  differentiators: [
    "Flat pricing: Pro is $3/mo for 50 monitors at 30-second checks, whatever the check volume.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Escalation policies on Team and Business route an unacknowledged alert to the next person after a delay you set.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you want AI-written incident summaries, Phare has them built in.",
    "If your usage is low, Phare's free Hobby tier or usage-based Scale plan could cost less than a flat plan.",
    "If you want unlimited members and status pages with custom domains on a free tier, Phare includes them.",
  ],
}
